import React from "react";

import { useTheme } from "@emotion/react";
import Cube from "@hl/base-components/lib/assets/icons.generated/Cube";
import Eye from "@hl/base-components/lib/assets/icons.generated/Eye";
import CursorClick01 from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Editor/CursorClick01";
import Image from "@hl/base-components/lib/assets/icons.generated/Image";
import LayersThree from "@hl/base-components/lib/assets/icons.generated/LayersThree";
import { OUTLINE_COLOR } from "@hl/base-components/lib/theme/button";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { envConfig } from "@hl/shared-features/lib/config";
import {
  Anchor,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  Loader,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";

import {
  GetCollectionTagsAndCategoriesQuery,
  useGetCollectionTagsAndCategoriesQuery,
} from "~features/MintPage/components/DetailSection/tags-categories/queries.graphql.generated";

export type TagsCategoriesDetailsProps = {
  collectionId: string;
  compact?: boolean;
};

type CategoriesType =
  GetCollectionTagsAndCategoriesQuery["getPublicCollectionDetails"]["userCategories"];

const categoriesToFind = [
  {
    type: "COLLECTION_TYPE",
    label: "Collection type",
    icon: <LayersThree height={16} width={16} />,
  },
  { type: "STYLE", label: "Style", icon: <Eye height={16} width={16} /> },
  { type: "SUBJECT", label: "Subject", icon: <Image height={16} width={16} /> },
  { type: "FORMAT", label: "Format", icon: <Cube height={16} width={16} /> },
  {
    type: "TECHNOLOGY",
    label: "Technology",
    icon: <CursorClick01 height={16} width={16} />,
  },
  { type: "HIGHLIGHTED" },
];

export const TagsCategoriesDetails = ({
  collectionId,
  compact = false,
}: TagsCategoriesDetailsProps) => {
  const theme = useTheme();
  const { data, loading } = useGetCollectionTagsAndCategoriesQuery({
    variables: { collectionId },
  });

  const categories = data?.getPublicCollectionDetails.userCategories || [];
  const tags = data?.getPublicCollectionDetails.userTags || [];

  if (loading) return <Loader />;

  const findCategories = (type: string) =>
    categories
      .filter((category) => category.type === type)
      .sort((a, b) => a.name.localeCompare(b.name));

  const tagLink = (slug: string) =>
    `${envConfig.home.baseUrl}/explore/tags?tags=${encodeURI(slug)}`;

  if (compact) {
    if (categories.length === 0 && tags.length === 0) return <></>;
    return (
      <Group spacing={"xs"}>
        {categoriesToFind.map((category) => (
          <CategoryPill
            key={category.type}
            categories={findCategories(category.type)}
          />
        ))}
        {tags.map((tag) => (
          <Anchor
            py={4}
            px={8}
            bg={theme.colors.secondaryBackground[0]}
            key={tag.slug}
            color={OUTLINE_COLOR}
            href={tagLink(tag.slug)}
            td="none !important"
            sx={{ borderRadius: 4 }}
          >
            <Text
              fw={WEIGHT_BOLD}
              color={theme.colors.invertedSecondaryText[0]}
              size="xs"
            >
              # {tag.name}
            </Text>
          </Anchor>
        ))}
      </Group>
    );
  }

  return (
    <Stack pt={6}>
      {categoriesToFind.map((category) => (
        <CategoryRow
          categories={findCategories(category.type)}
          label={category.label}
          icon={category.icon}
          key={category.type}
        />
      ))}
      {!!tags.length &&
        tags.map((tag) => (
          <Button
            w="100%"
            component="a"
            size="xs"
            key={tag.slug}
            color={OUTLINE_COLOR}
            href={tagLink(tag.slug)}
            fullWidth
          >
            # {tag.name}
          </Button>
        ))}
    </Stack>
  );
};

const CategoryRow = ({
  categories,
  label,
  icon,
}: {
  categories: CategoriesType;
  label?: string;
  icon?: React.ReactNode;
}) => {
  if (!categories || categories.length === 0) return <></>;
  const link = (slug: string) =>
    `${envConfig.home.baseUrl}/explore/categories?categories=${encodeURI(
      slug
    )}`;
  return (
    <>
      <Grid align="center" gutter={8}>
        {icon && (
          <Grid.Col span="content">
            <Box w={16} h={16}>
              <Tooltip label={label}>{icon}</Tooltip>
            </Box>
          </Grid.Col>
        )}
        <Grid.Col span="auto">
          <Group spacing={8}>
            {categories.map((category) => (
              <Button
                component="a"
                size="xs"
                key={category.slug}
                color={OUTLINE_COLOR}
                href={link(category.slug)}
              >
                {category.name}
              </Button>
            ))}
          </Group>
        </Grid.Col>
      </Grid>
      <Divider />
    </>
  );
};

const CategoryPill = ({ categories }: { categories: CategoriesType }) => {
  const theme = useTheme();
  if (!categories || categories.length === 0) return <></>;
  const link = (slug: string) =>
    `${envConfig.home.baseUrl}/explore/categories?categories=${encodeURI(
      slug
    )}`;
  return (
    <>
      {categories.map((category) => (
        <Anchor
          py={4}
          px={8}
          bg={theme.colors.secondaryBackground[0]}
          key={category.slug}
          href={link(category.slug)}
          sx={{ borderRadius: 4 }}
          td="none !important"
        >
          <Text
            fw={WEIGHT_BOLD}
            color={theme.colors.invertedSecondaryText[0]}
            size="xs"
          >
            {category.name}
          </Text>
        </Anchor>
      ))}
    </>
  );
};
